import { useCallback, useState } from "react";

import { submit } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import { boxesOf, phaseOf, roundNumberOf } from "./show";

const PHASE_SAYS: Record<string, string> = {
  idle: "Nothing is running. Start a round.",
  voting: "Voting. The question is still hidden.",
  asking: "The question is up and the buzzers are live.",
  answering: "Somebody buzzed in. Your call.",
};

/**
 * Running the show: start a round, mark the answer, put everything back.
 *
 * Reads the stage and writes the clock. panel_stage draws and submits nothing.
 */
export function ShowHost({ clockMount, stageMount }: { clockMount: string; stageMount: string }) {
  const stage = useCanvasState(stageMount);
  const [refused, setRefused] = useState<string | null>(null);

  const send = useCallback((mount: string, submission: string, value: string) => {
    setRefused(null);
    submit(mount, submission, value).catch((error: unknown) => {
      setRefused(error instanceof Error ? error.message : "that was refused");
    });
  }, []);

  const advance = useCallback(
    (value: string) => send(clockMount, "advance", value),
    [send, clockMount],
  );

  if (stage.error !== null) {
    return <Problem what={stage.error} />;
  }

  const phase = phaseOf(stage.state);
  const boxes = boxesOf(stage.state);
  const buzzed = boxes.find((one) => one.buzzed);
  const waiting = phase === "answering";

  return (
    <main style={page}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Run the show</h1>
        <span style={{ opacity: 0.7 }}>round {roundNumberOf(stage.state)}</span>
      </header>

      <p style={{ margin: "12px 0 20px", opacity: 0.8 }}>{PHASE_SAYS[phase]}</p>

      <button type="button" style={{ ...button, background: "#5b6bff" }} onClick={() => advance("open")}>
        {phase === "idle" ? "Start the round" : "Start the next round"}
      </button>

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <button
          type="button"
          style={{ ...button, flex: 1, background: waiting ? "#2e9e63" : "#2c3350" }}
          disabled={!waiting}
          onClick={() => advance("accept")}
        >
          Right answer
        </button>
        <button
          type="button"
          style={{ ...button, flex: 1, background: waiting ? "#a3423f" : "#2c3350" }}
          disabled={!waiting}
          onClick={() => advance("reject")}
        >
          Wrong answer
        </button>
      </div>

      <p style={{ minHeight: 20, margin: "12px 0", opacity: 0.75 }}>
        {buzzed === undefined
          ? "Nobody has buzzed."
          : `${buzzed.label} buzzed in${buzzed.charged ? ", charged, so a right answer pays double" : ""}.`}
      </p>

      <h2 style={{ fontSize: 15, opacity: 0.7, margin: "24px 0 8px" }}>The boxes</h2>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {boxes.map((box) => (
          <li key={box.place} style={row}>
            <span style={{ width: 62, opacity: 0.6 }}>box {box.place}</span>
            <span style={{ flex: 1 }}>{box.taken ? box.label : "empty"}</span>
            <span style={{ opacity: 0.8 }}>{marks(box)}</span>
            <strong style={{ width: 46, textAlign: "right" }}>{Math.round(box.points)}</strong>
          </li>
        ))}
      </ul>

      <h2 style={{ fontSize: 15, opacity: 0.7, margin: "24px 0 8px" }}>Everything</h2>
      <button type="button" style={{ ...button, background: "#3a2140" }} onClick={() => advance("reset")}>
        Start the show again
      </button>
      <p style={{ fontSize: 12, opacity: 0.55, marginTop: 8 }}>
        Clears every score, effect and vote. Contestants keep the box they chose.
      </p>

      {refused !== null && <p style={{ color: "#ff8f8f", marginTop: 16 }}>{refused}</p>}
    </main>
  );
}

function marks(box: {
  charged: boolean;
  slowed: boolean;
  buzzed: boolean;
  correct: boolean;
  wrong: boolean;
}): string {
  const said = [];
  if (box.charged) said.push("charged");
  if (box.slowed) said.push("slowed");
  if (box.buzzed) said.push("buzzed");
  if (box.correct) said.push("right");
  if (box.wrong) said.push("wrong");
  return said.join(", ");
}

function Problem({ what }: { what: string }) {
  return (
    <main style={page}>
      <h1 style={{ fontSize: 20 }}>This host canvas cannot be read</h1>
      <p style={{ opacity: 0.8 }}>{what}</p>
      <p style={{ opacity: 0.6 }}>
        Sign in as the moderator first. The controls name role:moderator, so nobody else may run the
        show.
      </p>
    </main>
  );
}

const page: React.CSSProperties = {
  maxWidth: 520,
  margin: "0 auto",
  padding: "32px 20px 60px",
  fontFamily: "system-ui, sans-serif",
};

const button: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  fontSize: 16,
  fontWeight: 700,
  color: "#f4f7ff",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
};

const row: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  padding: "10px 12px",
  marginBottom: 6,
  borderRadius: 8,
  background: "rgba(255,255,255,0.05)",
};
